import equals from 'ramda/es/equals';
import type { ViewportConfigurationEntry } from './steppedHPTypes';

/** Output-only: how the server matched a viewport. A save never sends it. */
const OUTPUT_ONLY_FIELDS = new Set(['match_justification']);

/** Keeps what a save would store: drops null, empty-array and output-only fields. */
function compactViewportEntry(
  entry: ViewportConfigurationEntry
): Partial<ViewportConfigurationEntry> {
  const compacted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(entry)) {
    if (value == null || OUTPUT_ONLY_FIELDS.has(key)) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    compacted[key] = value;
  }
  return compacted;
}

/** True when two viewport entries describe the same effective assignment. */
export function isViewportEntryEquivalent(
  a: ViewportConfigurationEntry | undefined,
  b: ViewportConfigurationEntry | undefined
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return equals(compactViewportEntry(a), compactViewportEntry(b));
}

/** True when two viewport records name the same viewports with equivalent entries. */
export function areViewportRecordsEquivalent(
  a: Record<string, ViewportConfigurationEntry> | undefined,
  b: Record<string, ViewportConfigurationEntry> | undefined
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  const ids = Object.keys(a);
  return (
    ids.length === Object.keys(b).length &&
    ids.every((id) => isViewportEntryEquivalent(a[id], b[id]))
  );
}
